import { CartUpdateEvent } from '@theme/events';

const BRIDGE_SOURCE = 'teeinblue-cart-sync';
const TEEINBLUE_ADD_BUTTON_SELECTOR = '#teeAtcButton, #teeinblue-atc-button';
const CART_POLL_INTERVAL = 400;
const CART_POLL_TIMEOUT = 15000;

let syncPromise = null;
let syncTimer = 0;
let lastSyncedCount = null;
let lastSyncedAt = 0;

function cartJsonUrl() {
  const cartUrl = globalThis.Theme?.routes?.cart_url || '/cart';
  return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
}

async function fetchCart() {
  const response = await fetch(cartJsonUrl(), {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Unable to refresh the cart (${response.status}).`);
  }

  return response.json();
}

function getItemCount(cart) {
  if (Number.isFinite(Number(cart?.item_count))) {
    return Number(cart.item_count);
  }

  return (cart?.items || []).reduce((total, item) => total + (Number(item.quantity) || 0), 0);
}

function getVisibleItemCount() {
  const count = document.querySelector('cart-icon [ref="cartBubbleCount"]')?.textContent?.trim();
  return Number.parseInt(count || '0', 10) || 0;
}

function publishCart(cart) {
  const itemCount = getItemCount(cart);
  const now = Date.now();

  // Teeinblue can call more than one compatibility hook for the same add.
  if (itemCount === lastSyncedCount && now - lastSyncedAt < 1000) return;

  lastSyncedCount = itemCount;
  lastSyncedAt = now;

  document.dispatchEvent(
    new CartUpdateEvent(cart, BRIDGE_SOURCE, {
      source: BRIDGE_SOURCE,
      itemCount,
    })
  );
}

async function syncCart() {
  if (syncPromise) return syncPromise;

  syncPromise = fetchCart()
    .then((cart) => {
      publishCart(cart);
      return cart;
    })
    .catch((error) => {
      console.warn('[Lil Luxe] Teeinblue cart refresh failed.', error);
      return null;
    })
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

function scheduleCartSync(delay = 120) {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(syncCart, delay);
}

async function watchForCartIncrease(startingCount) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < CART_POLL_TIMEOUT) {
    await new Promise((resolve) => window.setTimeout(resolve, CART_POLL_INTERVAL));

    try {
      const cart = await fetchCart();
      if (getItemCount(cart) > startingCount) {
        publishCart(cart);
        return;
      }
    } catch (_) {
      // A later poll or the official Shopify.onItemAdded hook can still complete the refresh.
    }
  }
}

// Teeinblue's supported compatibility option calls this hook after an item is added.
globalThis.Shopify = globalThis.Shopify || {};
const previousOnItemAdded = globalThis.Shopify.onItemAdded;

globalThis.Shopify.onItemAdded = function (...args) {
  let result;

  try {
    if (typeof previousOnItemAdded === 'function') {
      result = previousOnItemAdded.apply(this, args);
    }
  } finally {
    scheduleCartSync();
  }

  return result;
};

// Fallback for stores where Teeinblue's notification option has not yet been enabled.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(TEEINBLUE_ADD_BUTTON_SELECTOR)) return;

    void watchForCartIncrease(getVisibleItemCount());
  },
  true
);

for (const eventName of ['teeinblue-event-after-cart-added', 'tee_bundle:bundle:added', 'cart:refresh']) {
  document.addEventListener(eventName, () => scheduleCartSync());
}
